import { Workspace, WorkspaceCompany, WorkspaceRole } from "@/types/workspace";

/**
 * Landing resolution (AITS-540).
 *
 * The Home screen is gone: `/` no longer renders a page, it resolves to a
 * company view. Two independent decisions feed that:
 *
 *   1. which workspace  — `pickLandingWorkspace`, consumed by WorkspaceContext
 *      on boot when nothing is persisted yet.
 *   2. which company    — `pickLandingCompany`, consumed by the `/` resolver
 *      and by the sidebar (so its company-scoped entries stay live off the
 *      /reports/:id routes, e.g. on Settings).
 *
 * Both are pure so they can be unit-tested without a router or a network.
 */

const LAST_COMPANY_KEY_PREFIX = "lastCompanyId:";

const lastCompanyKey = (workspaceId: number): string =>
  `${LAST_COMPANY_KEY_PREFIX}${workspaceId}`;

/** The company the user last had open in this workspace, if we've seen one. */
export const readLastCompanyId = (
  workspaceId: number | null | undefined,
): number | null => {
  if (workspaceId == null || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(lastCompanyKey(workspaceId));
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeLastCompanyId = (
  workspaceId: number | null | undefined,
  companyId: number | null | undefined,
): void => {
  if (workspaceId == null || companyId == null || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(lastCompanyKey(workspaceId), String(companyId));
  } catch {
    // Private-mode / quota — the fallbacks below still resolve a company.
  }
};

/**
 * Recency proxy for "when did this company last have a report run".
 *
 * `lastReportId` is the id of the company's newest report (the backend picks it
 * with `order_by("company_id", "-created_ts").distinct("company_id")`), and
 * report ids are a Postgres sequence, so a higher id is always a later report.
 * We rank on the id rather than the timestamp the API also returns because that
 * field is overloaded — it carries the report's `created_ts` for companies that
 * have one and the *workspace link's* `created_at` for companies that don't, in
 * two different shapes. The id has no such ambiguity.
 */
const reportRecency = (company: WorkspaceCompany): number => company.lastReportId ?? -1;

/**
 * Will opening this company's dashboard actually show a report?
 *
 * `status` is the *latest* report's status, so this is deliberately
 * conservative: a company mid-re-run reads as not ready even though last
 * month's report is still there. That only demotes it in the ranking below, and
 * if it's the sole candidate it still wins — at which point the dashboard falls
 * back to its newest completed report anyway.
 *
 * The case this exists for is the company whose *only* report never finished:
 * landing there would show a progress screen instead of data.
 */
const isViewable = (company: WorkspaceCompany): boolean =>
  company.lastReportId != null && company.status === "completed";

const byNewestReport = (a: WorkspaceCompany, b: WorkspaceCompany): number =>
  Number(isViewable(b)) - Number(isViewable(a)) ||
  reportRecency(b) - reportRecency(a) ||
  a.company_id - b.company_id;

/**
 * The company to open for a workspace.
 *
 * Sticky first: whichever company the user last had open here, so the landing
 * spot doesn't move under them when a scheduled report finishes elsewhere. The
 * stored id is validated against the workspace's own company list, so a stale
 * or cross-workspace value can never produce a link into a workspace the user
 * isn't in.
 *
 * With no history — a first login, a new device — it falls back to the company
 * whose report ran most recently, preferring one that has a finished report so
 * the very first screen isn't a "still building" placeholder.
 */
export const pickLandingCompany = (
  workspace: Workspace | null | undefined,
  lastCompanyId: number | null,
): WorkspaceCompany | null => {
  const companies = workspace?.companies ?? [];
  if (companies.length === 0) return null;

  if (lastCompanyId != null) {
    const remembered = companies.find((c) => c.company_id === lastCompanyId);
    // An explicit last-opened company always wins. A mid-generation one is not
    // a dead end: the dashboard falls back to its newest completed report, and
    // shows a "still building" state when it genuinely has none.
    if (remembered) return remembered;
  }

  return [...companies].sort(byNewestReport)[0] ?? null;
};

/** Newest report in a workspace, or -1 when it has no reports at all. */
const workspaceRecency = (workspace: Workspace): number =>
  (workspace.companies ?? []).reduce(
    (newest, company) => Math.max(newest, reportRecency(company)),
    -1,
  );

export const workspaceIsBillingEnded = (workspace: Workspace): boolean =>
  workspace.access_state?.lock_reason != null;

/** Whether this workspace can participate in client-side workspace selection. */
export const workspaceHasAccess = (workspace: Workspace): boolean =>
  !workspaceIsBillingEnded(workspace);

const workspaceCreatedAt = (workspace: Workspace): number => {
  if (!workspace.created_at) return Number.NEGATIVE_INFINITY;
  const timestamp = Date.parse(workspace.created_at);
  return Number.isNaN(timestamp) ? Number.NEGATIVE_INFINITY : timestamp;
};

export const pickNextActiveWorkspace = (
  workspaces: Workspace[],
): Workspace | null =>
  [...workspaces]
    .filter(workspaceHasAccess)
    .sort(
      (a, b) => workspaceCreatedAt(b) - workspaceCreatedAt(a) || b.id - a.id,
    )[0] ?? null;

/**
 * The workspace to open on boot.
 *
 * Preferring the owned workspace outright (the old behaviour) stranded invited
 * members: signup provisions everyone a personal workspace, so someone invited
 * into a colleague's workspace owns an empty one and landed there, seeing
 * nothing. So we only consider workspaces that have something in them, and fall
 * back to ownership as a tie-breaker rather than as the primary rule.
 *
 * The persisted choice is honoured, but *only if it still has something in it*.
 * The ordinary invite arrives after signup, by which point the empty personal
 * workspace is already persisted — deferring to it unconditionally would mean
 * the rule above only ever helped on a first-ever login.
 *
 * Known trade-off: this runs on every boot, and a reload is a boot. Switching
 * into an empty workspace survives navigation (WorkspaceContext keeps the
 * active workspace across mid-session refreshes) but NOT a reload — you come
 * back to a populated one. Accepted because an empty workspace holds nothing
 * but "add your first company" and the sidebar switcher restores it in a click,
 * whereas an invited member landing on a blank screen has nowhere to go.
 */
export const pickLandingWorkspace = (
  workspaces: Workspace[],
  persistedWorkspaceId: string | null,
): Workspace | null => {
  const accessibleWorkspaces = workspaces.filter(workspaceHasAccess);
  if (accessibleWorkspaces.length === 0) return null;

  const populated = accessibleWorkspaces.filter(
    (w) => (w.companies ?? []).length > 0,
  );

  if (persistedWorkspaceId) {
    const persisted = accessibleWorkspaces.find(
      (w) => String(w.id) === persistedWorkspaceId,
    );
    if (persisted && (populated.length === 0 || (persisted.companies ?? []).length > 0)) {
      return persisted;
    }
  }

  const candidates = populated.length > 0 ? populated : accessibleWorkspaces;

  // Among workspaces with content, the user's own wins over one they were
  // invited into — landing in someone else's workspace when you have your own
  // work would be surprising.
  const owned = candidates.filter((w) => w.role === WorkspaceRole.Owner);
  const pool = owned.length > 0 ? owned : candidates;

  return (
    [...pool].sort(
      (a, b) => workspaceRecency(b) - workspaceRecency(a) || a.id - b.id,
    )[0] ?? null
  );
};

/**
 * Where a company opens.
 *
 * `/reports/:companyId` (no report id) lets the dashboard resolve the period. It
 * resolves to the newest COMPLETED report, and renders the "generate this
 * month's report" prompt above it whenever the current month has none — so you
 * land on real data and still get the call to action. Naming a report id here
 * would pin the landing to one month and lose that.
 *
 * A company with no report yet opens the same place: that page carries the
 * "generate a report" prompt with the company and workspace prefilled, which is
 * the whole point of landing there. It used to divert to the buyer list.
 */
export const companyDestination = (company: WorkspaceCompany): string =>
  `/reports/${company.company_id}`;
